import { createRoute } from '@tanstack/react-router';
import { Route as rootRoute } from './__root';
import { Helmet } from 'react-helmet-async';
import { Store, Heart, Shield, Globe } from 'lucide-react';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/about',
  component: AboutPage,
});

function AboutPage() {
  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>About Us - Cardston Market</title>
        <meta name="description" content="Cardston Market was built on the belief that local communities thrive when local makers are given a platform to shine. Learn about our mission to connect artisans with shoppers." />
        <link rel="canonical" href="https://www.cardstonmarket.com/about" />
        <meta property="og:title" content="About Us - Cardston Market" />
        <meta property="og:description" content="Cardston Market was built on the belief that local communities thrive when local makers are given a platform to shine." />
        <meta property="og:url" content="https://www.cardstonmarket.com/about" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About Cardston Market",
          "description": "Cardston Market was built on the belief that local communities thrive when local makers are given a platform to shine. We connect creative artisans with thoughtful shoppers.",
          "url": "https://www.cardstonmarket.com/about",
          "mainEntity": {
            "@type": "Organization",
            "@id": "https://www.cardstonmarket.com/#organization"
          },
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": ["h1", "h2", "h3", "p"]
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.cardstonmarket.com/" },
            { "@type": "ListItem", "position": 2, "name": "About", "item": "https://www.cardstonmarket.com/about" }
          ]
        })}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-24 bg-neutral-900 border-b border-neutral-800">
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1542838132-92c53300491e?q=80&w=1974&auto=format&fit=crop" 
            alt="Local artisans selling handmade goods at Cardston Market in Cardston, Alberta" 
            className="w-full h-full object-cover opacity-20"
            loading="lazy"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-serif font-medium text-white mb-6 tracking-tight">
            Our Story
          </h1>
          <p className="text-lg md:text-xl text-neutral-300 max-w-2xl mx-auto font-light leading-relaxed">
            Cardston Market was built on the belief that local communities thrive when local makers are given a platform to shine. We connect creative artisans with thoughtful shoppers.
          </p>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-white dark:bg-neutral-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-serif font-medium text-neutral-900 dark:text-white mb-4">Our Values</h2>
            <div className="w-16 h-1 bg-neutral-200 dark:bg-neutral-800 mx-auto rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-5xl mx-auto">
            <div className="text-center group">
              <div className="bg-neutral-50 dark:bg-neutral-900 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <Store className="w-8 h-8 text-neutral-900 dark:text-white stroke-[1.5]" />
              </div>
              <h3 className="text-xl font-medium text-neutral-900 dark:text-white mb-3">Support Local</h3>
              <p className="text-neutral-500 dark:text-neutral-400 font-light leading-relaxed">
                By shopping on Cardston Market, you are keeping money in the local economy and supporting independent businesses.
              </p>
            </div>
            
            <div className="text-center group">
              <div className="bg-neutral-50 dark:bg-neutral-900 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <Shield className="w-8 h-8 text-neutral-900 dark:text-white stroke-[1.5]" />
              </div>
              <h3 className="text-xl font-medium text-neutral-900 dark:text-white mb-3">Quality & Trust</h3>
              <p className="text-neutral-500 dark:text-neutral-400 font-light leading-relaxed">
                We work closely with our vendors to ensure quality products and transparent communication through our messaging and Q&A tools.
              </p>
            </div>
            
            <div className="text-center group">
              <div className="bg-neutral-50 dark:bg-neutral-900 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <Globe className="w-8 h-8 text-neutral-900 dark:text-white stroke-[1.5]" />
              </div>
              <h3 className="text-xl font-medium text-neutral-900 dark:text-white mb-3">Sustainable Impact</h3>
              <p className="text-neutral-500 dark:text-neutral-400 font-light leading-relaxed">
                Reduced shipping distances and support for handmade items mean a smaller carbon footprint and a better planet.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer Banner */}
      <section className="py-24 bg-neutral-50 dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800">
         <div className="max-w-3xl mx-auto px-4 text-center">
            <Heart className="w-12 h-12 mx-auto text-neutral-400 dark:text-neutral-600 mb-6 stroke-[1.5]" />
            <h2 className="text-2xl font-serif font-medium text-neutral-900 dark:text-white mb-4">Join our community</h2>
            <p className="text-neutral-500 dark:text-neutral-400 font-light mb-8">
               Whether you're looking to buy unique goods or sell your own creations, there's a place for you here.
            </p>
         </div>
      </section>
    </div>
  );
}
